import React from 'react';
import { Link } from 'react-router-dom';
import './GuideIntegrazioneCsv.css';

const GuideIntegrazioneCsv = () => {
    const handleDownloadExample = () => {
        // Scarica il file CSV statico
        const link = document.createElement('a');
        link.href = '/esempio_feed_prodotti.csv';
        link.download = 'esempio_feed_prodotti.csv';
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="guide-container">
            <div className="guide-header">
                <h1>Guida Integrazione CSV</h1>
                <p>Scopri come caricare i tuoi prodotti tramite file CSV</p>
            </div>

            <div className="guide-content">
                <div className="tab-content">
                    <h2>Panoramica</h2>
                    <p>
                        L'integrazione CSV ti permette di caricare i tuoi prodotti in modo semplice e veloce. 
                        Il sistema è progettato per essere flessibile e supportare diversi formati di dati.
                    </p>
                    
                    <p>
                        <strong>Caratteristiche principali:</strong>
                    </p>
                    <ul>
                        <li><strong>Mapping flessibile:</strong> Usa i nomi delle colonne che preferisci</li>
                        <li><strong>Fallback automatici:</strong> Il sistema gestisce automaticamente i campi mancanti</li>
                        <li><strong>Immagini automatiche:</strong> Download e ottimizzazione automatica</li>
                        <li><strong>File grandi:</strong> Gestisce file con centinaia di migliaia di righe</li>
                        <li><strong>Normalizzazione testo:</strong> Correzione automatica caratteri speciali</li>
                    </ul>

                    <div className="important-note">
                        <h3>⚠️ Note Importanti</h3>
                        <ul>
                            <li>Utilizza <strong>virgole</strong> per separare i valori nelle colonne multiple</li>
                            <li>Gli <strong>URL delle immagini</strong> devono essere accessibili pubblicamente</li>
                            <li>I <strong>prezzi</strong> devono essere numeri positivi</li>
                            <li>Lo <strong>stock</strong> deve essere un numero intero non negativo (campo obbligatorio)</li>
                        </ul>
                    </div>

                    <h2>Campi del File CSV</h2>
                    
                    <h3>Campi Obbligatori</h3>
                    <p>Questi campi sono necessari per ogni riga del file CSV:</p>

                    <div className="fields-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Campo</th>
                                    <th>Alias Supportati</th>
                                    <th>Descrizione</th>
                                    <th>Esempio</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>product_id</code></td>
                                    <td>id, productid, product id</td>
                                    <td>ID univoco del prodotto</td>
                                    <td>PROD001</td>
                                </tr>
                                <tr>
                                    <td><code>title</code></td>
                                    <td>name, product_name, product name</td>
                                    <td>Titolo del prodotto</td>
                                    <td>Maglietta Basic</td>
                                </tr>
                                <tr>
                                    <td><code>price</code></td>
                                    <td>cost, amount</td>
                                    <td>Prezzo della variante</td>
                                    <td>29.99</td>
                                </tr>
                                <tr>
                                    <td><code>stock *</code></td>
                                    <td>quantity, inventory, available_quantity</td>
                                    <td>Quantità disponibile (obbligatorio)</td>
                                    <td>50</td>
                                </tr>
                                <tr>
                                    <td><code>availability *</code></td>
                                    <td>availability, status</td>
                                    <td>Disponibilità (in_stock, out_of_stock, true, false). Il sistema ha bisogno o di stock o di availability</td>
                                    <td>in_stock</td>
                                </tr>
                                <tr>
                                    <td><code>product_link</code></td>
                                    <td>url, link, product url</td>
                                    <td>Link al prodotto sul sito</td>
                                    <td>https://shop.com/product1</td>
                                </tr>
                                <tr>
                                    <td><code>images</code></td>
                                    <td>image, image_url, photos</td>
                                    <td>URL delle immagini (solo prima elaborata)</td>
                                    <td>https://example.com/image1.jpg</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <h3>Campi Opzionali</h3>
                    <p>Questi campi migliorano la presentazione dei tuoi prodotti:</p>

                    <div className="fields-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Campo</th>
                                    <th>Alias Supportati</th>
                                    <th>Descrizione</th>
                                    <th>Esempio</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>variant_id</code></td>
                                    <td>variantid, variant id, sku</td>
                                    <td>ID univoco della variante (fallback: product_id)</td>
                                    <td>VAR001</td>
                                </tr>
                                <tr>
                                    <td><code>description</code></td>
                                    <td>desc, product_description</td>
                                    <td>Descrizione del prodotto (fallback: title)</td>
                                    <td>Maglietta in cotone 100%</td>
                                </tr>
                                <tr>
                                    <td><code>brand</code></td>
                                    <td>vendor, manufacturer</td>
                                    <td>Marca del prodotto</td>
                                    <td>BrandName</td>
                                </tr>
                                <tr>
                                    <td><code>compare_at_price</code></td>
                                    <td>compare price, original_price, msrp</td>
                                    <td>Prezzo originale (solo se maggiore del price)</td>
                                    <td>39.99</td>
                                </tr>
                                <tr>
                                    <td><code>sku</code></td>
                                    <td>stock_keeping_unit, product_code</td>
                                    <td>Codice SKU della variante (fallback: variant_id)</td>
                                    <td>SKU001</td>
                                </tr>
                                <tr>
                                    <td><code>barcode</code></td>
                                    <td>ean, upc, isbn</td>
                                    <td>Codice a barre/EAN</td>
                                    <td>1234567890123</td>
                                </tr>
                                <tr>
                                    <td><code>weight</code></td>
                                    <td>mass, product_weight</td>
                                    <td>Peso del prodotto</td>
                                    <td>200</td>
                                </tr>
                                <tr>
                                    <td><code>weight_unit</code></td>
                                    <td>weight unit, unit, mass_unit</td>
                                    <td>Unità di misura (g, kg, lb, oz) - opzionale, se non specificato considera kg</td>
                                    <td>g</td>
                                </tr>
                                <tr>
                                    <td><code>product_type</code></td>
                                    <td>type, category</td>
                                    <td>Tipo di prodotto</td>
                                    <td>Abbigliamento</td>
                                </tr>
                                <tr>
                                    <td><code>option_name_1</code></td>
                                    <td>option name 1, option1_name</td>
                                    <td>Nome prima opzione (es: Colore)</td>
                                    <td>Colore</td>
                                </tr>
                                <tr>
                                    <td><code>option_value_1</code></td>
                                    <td>option value 1, option1_value</td>
                                    <td>Valore prima opzione (es: Rosso)</td>
                                    <td>Rosso</td>
                                </tr>
                                <tr>
                                    <td><code>option_name_2</code></td>
                                    <td>option name 2, option2_name</td>
                                    <td>Nome seconda opzione (es: Taglia)</td>
                                    <td>Taglia</td>
                                </tr>
                                <tr>
                                    <td><code>option_value_2</code></td>
                                    <td>option value 2, option2_value</td>
                                    <td>Valore seconda opzione (es: S)</td>
                                    <td>S</td>
                                </tr>
                                <tr>
                                    <td><code>option_name_3</code></td>
                                    <td>option name 3, option3_name</td>
                                    <td>Nome terza opzione (es: Vestibilità)</td>
                                    <td>Vestibilità</td>
                                </tr>
                                <tr>
                                    <td><code>option_value_3</code></td>
                                    <td>option value 3, option3_value</td>
                                    <td>Valore terza opzione (es: Larga)</td>
                                    <td>Larga</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="special-features">
                        <h3>Caratteristiche Speciali</h3>
                        <div className="feature-list">
                            <div className="feature-item">
                                <h4>Opzioni Varianti</h4>
                                <p>Puoi specificare fino a 3 opzioni per variante (es: Colore, Taglia, Vestibilità). Se non specifichi opzioni, verrà usato "Title" / "Default Title".</p>
                            </div>
                            <div className="feature-item">
                                <h4>Unità di Misura</h4>
                                <p>Supporta g, kg, lb, oz. Se non specifichi l'unità, assume chilogrammi (nessuna conversione).</p>
                            </div>
                            <div className="feature-item">
                                <h4>Disponibilità</h4>
                                <p><code>stock</code> è obbligatorio (quantità numerica). <code>availability</code> è opzionale (valori: "in_stock", "out_of_stock", "true", "false"). Se availability è "in_stock" ma stock è 0, lo stock viene impostato a 10.</p>
                            </div>
                            <div className="feature-item">
                                <h4>Prezzi di Confronto</h4>
                                <p><code>compare_at_price</code> viene salvato solo se è maggiore del <code>price</code> corrente.</p>
                            </div>
                            <div className="feature-item">
                                <h4>Fallback Automatici</h4>
                                <p>Il sistema gestisce automaticamente i campi mancanti: variant_id → product_id, description → title, sku → variant_id.</p>
                            </div>
                            <div className="feature-item">
                                <h4>Immagini</h4>
                                <p>Se fornisci più URL di immagini separati da virgola, viene elaborata solo la prima immagine.</p>
                            </div>
                        </div>
                    </div>

                    <h2>Esempi di Utilizzo</h2>

                    <div className="examples-section">
                        <h3>Esempio 1: Prodotto Semplice (Solo Campi Obbligatori)</h3>
                        <div className="example-csv">
                            <pre>{`product_id,title,price,stock,product_link,images
PROD001,Maglietta Basic,29.99,50,https://shop.com/product1,https://example.com/image1.jpg`}</pre>
                        </div>
                        <p><strong>Risultato:</strong> Prodotto con prezzo 29.99, stock 50, disponibilità "in stock", variant_id = PROD001</p>
                    </div>

                    <div className="examples-section">
                        <h3>Esempio 2: Prodotto con Varianti</h3>
                        <div className="example-csv">
                            <pre>{`product_id,variant_id,title,price,stock,option_name_1,option_value_1,option_name_2,option_value_2
PROD001,VAR001,Maglietta Basic,29.99,50,Colore,Rosso,Taglia,S
PROD001,VAR002,Maglietta Basic,29.99,30,Colore,Rosso,Taglia,M
PROD001,VAR003,Maglietta Basic,29.99,20,Colore,Blu,Taglia,S`}</pre>
                        </div>
                        <p><strong>Risultato:</strong> Un prodotto con 3 varianti (Rosso/S, Rosso/M, Blu/S), prezzo min/max: 29.99, stock totale: 100</p>
                    </div>

                    <div className="examples-section">
                        <h3>Esempio 3: Prodotto Completo</h3>
                        <div className="example-csv">
                            <pre>{`product_id,variant_id,title,description,brand,price,compare_at_price,sku,barcode,weight,weight_unit,stock,availability,product_type,images,product_link,option_name_1,option_value_1,option_name_2,option_value_2
PROD001,VAR001,Maglietta Premium,Maglietta in cotone 100% di alta qualità,PremiumBrand,39.99,49.99,SKU001,1234567890123,200,g,50,in_stock,Abbigliamento,https://example.com/image1.jpg,https://shop.com/product1,Colore,Rosso,Taglia,S`}</pre>
                        </div>
                        <p><strong>Risultato:</strong> Prodotto completo con tutti i campi popolati, immagine scaricata automaticamente, brand capitalizzato</p>
                    </div>

                    <div className="examples-section">
                        <h3>Esempio 4: Gestione Disponibilità</h3>
                        <div className="example-csv">
                            <pre>{`product_id,title,price,availability,stock
PROD001,Prodotto A,29.99,in_stock,0
PROD002,Prodotto B,39.99,out_of_stock,0
PROD003,Prodotto C,59.99,,25`}</pre>
                        </div>
                        <p><strong>Risultato:</strong></p>
                        <ul>
                            <li>Prodotto A: availability "in stock", stock 10 (0 ma availability in_stock)</li>
                            <li>Prodotto B: availability "out of stock", stock 0</li>
                            <li>Prodotto C: availability "in stock", stock 25 (solo stock presente)</li>
                        </ul>
                    </div>

                    <div className="download-section">
                        <h2>Scarica File di Esempio</h2>
                        <p>Scarica un file CSV di esempio completo con tutti i campi per iniziare subito:</p>
                        
                        <button className="download-button" onClick={handleDownloadExample}>
                            📥 Scarica Esempio CSV
                        </button>
                        <p className="download-info">
                            Il file contiene esempi di prodotti con varianti, opzioni, immagini e tutti i campi opzionali.
                        </p>
                    </div>

                    <div className="file-info">
                        <h3>Informazioni sul File</h3>
                        <ul>
                            <li><strong>Formato:</strong> CSV con encoding UTF-8</li>
                            <li><strong>Separatore:</strong> Virgola (,)</li>
                            <li><strong>Intestazioni:</strong> Prima riga contiene i nomi delle colonne</li>
                            <li><strong>Esempi inclusi:</strong> Prodotti con varianti, opzioni, prezzi di confronto</li>
                        </ul>
                    </div>

                    <div className="usage-steps">
                        <h3>Come Utilizzare il File di Esempio</h3>
                        <ol>
                            <li>Scarica il file CSV di esempio</li>
                            <li>Apri il file con Excel o un editor di testo</li>
                            <li>Sostituisci i dati di esempio con i tuoi prodotti</li>
                            <li>Mantieni la struttura delle colonne</li>
                            <li>Salva il file in formato CSV</li>
                            <li>Carica il file nel tuo pannello seller</li>
                        </ol>
                    </div>
                </div>
            </div>

            <div className="guide-footer">
                <h3>Hai bisogno di aiuto?</h3>
                <p>Se hai domande sull'integrazione CSV, contatta il nostro team di supporto.</p>
                <Link to="/support" className="support-link">Contatta Supporto</Link>
            </div>
        </div>
    );
};

export default GuideIntegrazioneCsv; 